"use client";

import { useEffect, useEffectEvent, useRef } from "react";
import { TrackingEvents } from "../lib/analytics";
import { CONSENT_CHANGE_EVENT, readConsentSnapshot } from "../lib/consent";

const MIN_VISIBLE_PIXELS = 96;
const CLIPPING_OVERFLOWS = new Set(["auto", "clip", "hidden", "scroll"]);

function intersectRects(first, second) {
  const top = Math.max(first.top, second.top);
  const bottom = Math.min(first.bottom, second.bottom);
  const left = Math.max(first.left, second.left);
  const right = Math.min(first.right, second.right);
  return { top, bottom, left, right, width: Math.max(0, right - left), height: Math.max(0, bottom - top) };
}

export function getLiveClippedRect(element, windowObject) {
  if (!element || !windowObject) return null;
  let rect = intersectRects(element.getBoundingClientRect(), { top: 0, bottom: windowObject.innerHeight, left: 0, right: windowObject.innerWidth });
  for (let parent = element.parentElement; parent; parent = parent.parentElement) {
    const style = windowObject.getComputedStyle?.(parent);
    if (!style) continue;
    const clipsX = CLIPPING_OVERFLOWS.has(style.overflowX) || CLIPPING_OVERFLOWS.has(style.overflow);
    const clipsY = CLIPPING_OVERFLOWS.has(style.overflowY) || CLIPPING_OVERFLOWS.has(style.overflow);
    if (!clipsX && !clipsY) continue;
    const parentRect = parent.getBoundingClientRect();
    rect = intersectRects(rect, {
      top: clipsY ? parentRect.top : -Infinity,
      bottom: clipsY ? parentRect.bottom : Infinity,
      left: clipsX ? parentRect.left : -Infinity,
      right: clipsX ? parentRect.right : Infinity,
    });
  }
  return rect;
}

export function isMeaningfullyVisibleRect(rect) {
  return Boolean(rect && rect.height >= MIN_VISIBLE_PIXELS && rect.width > 0);
}

export function createFormViewObserver({ element, windowObject, hasAnalyticsConsent, onView }) {
  if (!element || !windowObject?.IntersectionObserver) return () => {};
  let intersectionObserver;
  let resizeObserver;
  let viewed = false;
  const maybeTrack = (rect) => {
    if (viewed || !isMeaningfullyVisibleRect(rect) || !hasAnalyticsConsent()) return;
    viewed = true;
    onView();
    intersectionObserver?.disconnect();
    resizeObserver?.disconnect();
  };
  const observeAtCurrentSize = () => {
    if (viewed) return;
    intersectionObserver?.disconnect();
    const height = Math.max(1, element.getBoundingClientRect().height);
    const visibleThreshold = Math.min(MIN_VISIBLE_PIXELS / height, 1);
    intersectionObserver = new windowObject.IntersectionObserver(
      ([entry]) => maybeTrack(entry?.intersectionRect),
      { threshold: [0, visibleThreshold, 1] },
    );
    intersectionObserver.observe(element);
  };
  const handleConsent = (event) => {
    if (event.detail?.analytics === true) maybeTrack(getLiveClippedRect(element, windowObject));
  };
  observeAtCurrentSize();
  if (windowObject.ResizeObserver) {
    resizeObserver = new windowObject.ResizeObserver(observeAtCurrentSize);
    resizeObserver.observe(element);
  }
  windowObject.addEventListener(CONSENT_CHANGE_EVENT, handleConsent);
  return () => {
    intersectionObserver?.disconnect();
    resizeObserver?.disconnect();
    windowObject.removeEventListener(CONSENT_CHANGE_EVENT, handleConsent);
  };
}

export function useFormTracking({ formRef, dimensions }) {
  const trackedStartRef = useRef(false);
  const trackView = useEffectEvent(() => TrackingEvents.trackFormStep("view", dimensions));
  useEffect(() => createFormViewObserver({
    element: formRef.current,
    windowObject: window,
    hasAnalyticsConsent: () => readConsentSnapshot().analytics,
    onView: () => trackView(),
  }), [formRef]);
  return function trackStart() {
    if (trackedStartRef.current) return;
    trackedStartRef.current = true;
    TrackingEvents.trackFormStep("start", dimensions);
  };
}
